import { supabase } from "../lib/supabase";
import { Booking, PaginationParams, StatusCount, BookingStatus } from "../types/booking";
import { SupabaseClient } from "@supabase/supabase-js";

// Type cast supabase to avoid TypeScript errors
const typedSupabase = supabase as unknown as SupabaseClient;

/**
 * Fetch bookings with filtering, pagination, and sorting
 */
export const fetchBookings = async (params: Partial<PaginationParams> = {}) => {
  try {
    const {
      page = 1,
      pageSize = 20,
      searchQuery,
      status,
      dateRange,
      sortField,
      sortOrder,
      companyId,
      transportTypeId,
      isConsolidation,
      containerId,
      agentId,
      bookingTypeId,
      blNumber,
      masterContainerId,
      hasInvoice,
      deliveryType,
    } = params;

    // Calculate range for pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    // Prepare the query
    let query = typedSupabase
      .from("bookings")
      .select(
        `
        *,
        transport_type_info:booking_transport_types(*),
        booking_type_info:bookings_type(*),
        company_info:company(*),
        supplier_info:booking_supplier(*),
        origin_port_info:booking_origin_port(*),
        destination_port_info:booking_destination_port(*),
        booking_incoterm_info:bookings_incoterm(*),
        agent_info:booking_agent(*),
        product_info:booking_product(*),
        container_info:containers(
          *,
          container_origin_port:booking_origin_port(*),
          container_destination_port:booking_destination_port(*),
          master_container_info:master_containers_containers(
            master_container_id,
            master_containers(
              id,
              reference
            )
          )
        ),
        invoice_info:booking_sales_invoice(
          id,
          created_at,
          currency,
          moneybird_payment_status,
          moneybird_invoice_date,
          lines:booking_sales_invoice_line(
            quantity,
            unit_price,
            vat_percentage
          )
        ),
        booking_delivery_info:booking_delivery(*)
      `,
        { count: "exact" }
      );

    // Add search filter if provided
    if (searchQuery) {
      query = query.or(
        `reference.ilike.%${searchQuery}%,id.eq.${
          !isNaN(parseInt(searchQuery)) ? parseInt(searchQuery) : 0
        }`
      );
    }

    // Add status filter if provided and not "all"
    if (status && status !== "all") {
      query = query.eq("status", status);
    }

    // Add date range filter if provided
    if (dateRange && dateRange.length === 2) {
      query = query
        .gte("created_at", dateRange[0])
        .lte("created_at", dateRange[1]);
    }

    // Add company filter if provided
    if (companyId) {
      query = query.eq("company_id", companyId);
    }

    // Add transport type filter if provided
    if (transportTypeId) {
      query = query.eq("transport_type", transportTypeId);
    }

    // Add agent filter if provided
    if (agentId) {
      query = query.eq("agent_id", agentId);
    }

    // Add booking type filter if provided
    if (bookingTypeId) {
      query = query.eq("booking_type", bookingTypeId);
    }

    // Add master container filter if provided
    if (masterContainerId) {
      // First get all container IDs that belong to this master container
      const { data: containerRelations, error: relationError } = await typedSupabase
        .from("master_containers_containers")
        .select("container_id")
        .eq("master_container_id", masterContainerId);

      if (relationError) throw relationError;

      const containerIds = containerRelations?.map(rel => rel.container_id).filter(Boolean) || [];

      if (containerIds.length > 0) {
        query = query.in("container_id", containerIds);
      } else {
        // No containers in this master container - return no results
        return { 
          data: [] as Booking[], 
          count: 0, 
          error: null 
        };
      }
    }
    // Add container filter if provided and no master container filter
    else if (containerId) {
      query = query.eq("container_id", containerId);
    }
    // Use isConsolidation if provided and neither containerId nor masterContainerId is provided
    else if (isConsolidation !== null) {
      if (isConsolidation === true) {
        // If consolidated, container_id should not be null
        query = query.not("container_id", "is", null);
      } else {
        // If not consolidated, container_id should be null
        query = query.is("container_id", null);
      }
    }

    // Add BL number filter if provided
    if (blNumber) {
      // For BL number filtering, we need to do a JOIN
      // First get all containers with this BL number
      const { data: containersWithBL, error: containerError } = await typedSupabase
        .from("containers")
        .select("id")
        .eq("bl_number", blNumber);

      if (containerError) throw containerError;

      if (containersWithBL && containersWithBL.length > 0) {
        // Get container IDs with matching BL number
        const containerIds = containersWithBL.map(c => c.id);
        // Filter bookings for these container IDs
        query = query.in("container_id", containerIds);
      } else {
        // No containers with this BL number - return no results
        return { 
          data: [] as Booking[], 
          count: 0, 
          error: null 
        };
      }
    }

    // Add sorting if provided
    if (sortField && sortOrder) {
      const order = sortOrder === "ascend" ? "asc" : "desc";
      query = query.order(sortField, { ascending: order === "asc" });
    } else {
      // Default sorting by creation date (newest first)
      query = query.order("created_at", { ascending: false });
    }

    // Execute query without pagination first to get all data for invoice filtering
    const { data: allData, error: queryError } = await query;

    if (queryError) throw queryError;

    let filteredData = allData as Booking[];

    // Apply delivery filtering if specified
    if (deliveryType !== null && deliveryType !== undefined) {
      if (deliveryType === 'storage') {
        // Filter for bookings WITHOUT delivery info (in storage)
        filteredData = filteredData.filter(booking => 
          !booking.booking_delivery_info || 
          (Array.isArray(booking.booking_delivery_info) && booking.booking_delivery_info.length === 0)
        );
      } else if (deliveryType === 'delivery') {
        // Filter for bookings WITH delivery info (for delivery)
        filteredData = filteredData.filter(booking => 
          booking.booking_delivery_info && 
          (Array.isArray(booking.booking_delivery_info) ? booking.booking_delivery_info.length > 0 : true)
        );
      }
    }

    // Apply invoice filtering if specified
    if (hasInvoice !== null && hasInvoice !== undefined) {
      filteredData = filteredData.filter((booking) => {
        const hasInvoiceData = booking.invoice_info && 
          ((Array.isArray(booking.invoice_info) && booking.invoice_info.length > 0) ||
           (typeof booking.invoice_info === 'object' && !Array.isArray(booking.invoice_info)));
        
        return hasInvoice ? hasInvoiceData : !hasInvoiceData;
      });
    }

    // Get the total count after filtering
    const totalCount = filteredData.length;

    // Apply pagination to filtered data
    const paginatedData = filteredData.slice(from, Math.min(to + 1, filteredData.length));

    return { 
      data: paginatedData as Booking[], 
      count: totalCount, 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching bookings:", error);
    return { 
      data: [] as Booking[], 
      count: 0, 
      error 
    };
  }
};

/**
 * Fetch pending bookings (status = "submitted")
 */
export const fetchPendingBookings = async () => {
  try {
    const { data, error } = await typedSupabase
      .from("bookings")
      .select(`
        *,
        transport_type_info:booking_transport_types(*),
        booking_type_info:bookings_type(*),
        company_info:company(*),
        supplier_info:booking_supplier(*),
        origin_port_info:booking_origin_port(*),
        destination_port_info:booking_destination_port(*),
        booking_incoterm_info:bookings_incoterm(*),
        agent_info:booking_agent(*),
        container_info:containers(
          *,
          container_origin_port:booking_origin_port(*),
          container_destination_port:booking_destination_port(*)
        )
      `)
      .eq("status", "submitted")
      .order("created_at", { ascending: false });

    if (error) throw error;

    return { 
      data: data as Booking[], 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching pending bookings:", error);
    return { 
      data: [] as Booking[], 
      error 
    };
  }
};

/**
 * Fetch booking status counts
 */
export const fetchBookingStatusCounts = async () => {
  try {
    console.log('Fetching booking status counts - direct method');
    
    // Define all valid statuses that we want counts for
    const validStatuses = [
      'concept', 'submitted', 'approved', 'rejected',
      'waiting_for_goods', 'goods_received_origin', 'in_transit', 
      'custom_clearance', 'out_for_delivery', 'delivered', 
      'added_to_stock', 'cancelled', 'archived'
    ];
    
    // Create a result array with correct typing
    const results: StatusCount[] = [];
    
    // Get count for each status - simple and reliable approach
    for (const status of validStatuses) {
      try {
        const { count, error } = await typedSupabase
          .from('bookings')
          .select('id', { count: 'exact', head: true })
          .eq('status', status);
          
        results.push({
          status,
          count: error ? 0 : (count ?? 0)
        });
      } catch (err) {
        // If there's an error, still add the status with count 0
        console.error(`Error fetching count for status ${status}:`, err);
        results.push({ status, count: 0 });
      }
    }
    
    console.log('Status counts fetched:', results);
    
    return { 
      data: results, 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching booking status counts:", error);
    
    // Return a complete set of statuses with zero counts in case of error
    const validStatuses = [
      'concept', 'submitted', 'approved', 'rejected',
      'waiting_for_goods', 'goods_received_origin', 'in_transit', 
      'custom_clearance', 'out_for_delivery', 'delivered', 
      'added_to_stock', 'cancelled', 'archived'
    ];
    
    const results = validStatuses.map(status => ({ status, count: 0 }));
    
    return { 
      data: results as StatusCount[], 
      error 
    };
  }
};

/**
 * Fetch total bookings count
 */
export const fetchTotalBookingsCount = async () => {
  try {
    const { count, error } = await supabase
      .from("bookings")
      .select("*", { count: "exact", head: true });

    if (error) throw error;

    return { 
      count: count || 0, 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching total bookings count:", error);
    return { 
      count: 0, 
      error 
    };
  }
};

/**
 * Approve a booking
 */
export const approveBooking = async (bookingId: number) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({ status: "approved" })
      .eq("id", bookingId);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error approving booking:", error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Reject a booking with a reason
 */
export const rejectBooking = async (bookingId: number, rejectionReason: string) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({
        status: "rejected",
        rejection_reason: rejectionReason,
      })
      .eq("id", bookingId);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error rejecting booking:", error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Fetch a single booking by ID with full details
 */
export const fetchBookingById = async (id: number) => {
  try {
    // First fetch the booking
    const { data, error } = await supabase
      .from("bookings")
      .select(`
        *,
        transport_type_info:booking_transport_types(*),
        booking_type_info:bookings_type(*),
        company_info:company(*),
        supplier_info:booking_supplier(*),
        origin_port_info:booking_origin_port(*),
        destination_port_info:booking_destination_port(*),
        booking_incoterm_info:bookings_incoterm(*),
        agent_info:booking_agent(*),
        container_info:containers(
          *,
          container_origin_port:booking_origin_port(*),
          container_destination_port:booking_destination_port(*)
        ),
        booking_delivery_info:booking_delivery(*),
        product_info:booking_product(*)
      `)
      .eq("id", id)
      .single();

    if (error) throw error;

    // If booking exists and has a container_id, fetch container details
    if (data && data.container_id) {
      const { data: containerData, error: containerError } = await supabase
        .from("containers")
        .select(`
          *,
          container_origin_port:booking_origin_port(*),
          container_destination_port:booking_destination_port(*)
        `)
        .eq("id", data.container_id)
        .single();

      if (containerError) {
        console.warn(`Error fetching container with ID ${data.container_id}:`, containerError);
      } else {
        // Add container data to booking
        data.container_info = containerData;
      }
    }

    return { 
      data: data as Booking, 
      error: null 
    };
  } catch (error) {
    console.error(`Error fetching booking with ID ${id}:`, error);
    return { 
      data: null, 
      error 
    };
  }
};

/**
 * Update a booking status
 */
export const updateBookingStatus = async (bookingId: number, status: BookingStatus, additionalData: Record<string, unknown> = {}) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({
        status,
        ...additionalData
      })
      .eq("id", bookingId);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error(`Error updating booking status to ${status}:`, error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Bulk update booking statuses
 */
export const bulkUpdateBookingStatus = async (bookingIds: number[], status: BookingStatus, additionalData: Record<string, unknown> = {}) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({
        status,
        ...additionalData
      })
      .in("id", bookingIds);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error(`Error bulk updating booking statuses to ${status}:`, error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Update booking details
 */
export const updateBookingDetails = async (
  bookingId: number, 
  data: { 
    reference?: string | null; 
    notes?: string | null;
    status?: string | null;
    origin_pickup_date?: string | null;
    transport_type?: number | null;
    booking_type?: number | null;
    booking_incoterm?: number | null;
  }
) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update(data)
      .eq("id", bookingId);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error updating booking details:", error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Update booking commercial invoice file
 */
export const updateBookingCommercialInvoice = async (
  bookingId: number, 
  filePath: string,
  appendToExisting: boolean = false
) => {
  try {
    // Get current file path if we need to append
    let updatedFilePath = filePath;
    
    if (appendToExisting) {
      const { data, error: fetchError } = await supabase
        .from("bookings")
        .select("commercial_invoice_file")
        .eq("id", bookingId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // If there's an existing file, append the new one with comma
      if (data?.commercial_invoice_file) {
        updatedFilePath = `${data.commercial_invoice_file},${filePath}`;
      }
    }
    
    const updateData = {
      commercial_invoice_file: updatedFilePath
    };

    const { error } = await supabase
      .from("bookings")
      .update(updateData)
      .eq("id", bookingId);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error updating commercial invoice:", error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Update a booking product
 * @param productId The ID of the product to update
 * @param productData The updated product data
 * @returns A promise that resolves to the update result
 */
export const updateBookingProduct = async (
  productId: number,
  productData: Partial<{
    name: string;
    hs_code: string;
    ean: string | null;
    pieces: number;
    cartons: number;
    gross_weight: number;
    cbm: number;
    value: number;
  }>
) => {
  try {
    const { data, error } = await supabase
      .from("booking_product")
      .update(productData)
      .eq("id", productId)
      .select();

    if (error) {
      console.error("Error updating booking product:", error);
      return { error, data: null };
    }

    return { data, error: null };
  } catch (error) {
    console.error("Exception updating booking product:", error);
    return { error, data: null };
  }
};

/**
 * Delete a booking product
 * @param productId The ID of the product to delete
 * @returns A promise that resolves to the deletion result
 */
export const deleteBookingProduct = async (productId: number) => {
  try {
    const { error } = await supabase
      .from("booking_product")
      .delete()
      .eq("id", productId);

    if (error) {
      console.error("Error deleting booking product:", error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error("Exception deleting booking product:", error);
    return { success: false, error };
  }
};

/**
 * Fetch all available booking agents
 */
export const fetchAgents = async () => {
  try {
    const { data, error } = await supabase
      .from("booking_agent")
      .select("*")
      .order("name", { ascending: true });

    if (error) throw error;

    return { 
      data, 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching agents:", error);
    return { 
      data: [], 
      error 
    };
  }
};

/**
 * Update a booking's agent
 */
export const updateBookingAgent = async (bookingId: number, agentId: number | null) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({ agent_id: agentId })
      .eq("id", bookingId);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error updating booking agent:", error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Update an existing delivery information
 */
export const updateDeliveryInfo = async (
  deliveryId: number,
  deliveryData: Partial<{
    street_name: string;
    house_number: string;
    city: string;
    postal_code: string;
    country: string;
    instructions: string | null;
  }>
) => {
  try {
    const { data, error } = await supabase
      .from("booking_delivery")
      .update(deliveryData)
      .eq("id", deliveryId)
      .select();

    if (error) {
      console.error("Error updating delivery information:", error);
      return { error, data: null };
    }

    return { data, error: null };
  } catch (error) {
    console.error("Exception updating delivery information:", error);
    return { error, data: null };
  }
};

/**
 * Create new delivery information for a booking
 */
export const createDeliveryInfo = async (
  bookingId: number,
  deliveryData: {
    street_name: string;
    house_number: string;
    city: string;
    postal_code: string;
    country: string;
    instructions?: string | null;
  }
) => {
  try {
    const { data, error } = await supabase
      .from("booking_delivery")
      .insert({
        booking_id: bookingId,
        ...deliveryData
      })
      .select();

    if (error) {
      console.error("Error creating delivery information:", error);
      return { error, data: null };
    }

    return { data, error: null };
  } catch (error) {
    console.error("Exception creating delivery information:", error);
    return { error, data: null };
  }
};

/**
 * Delete delivery information
 */
export const deleteDeliveryInfo = async (deliveryId: number) => {
  try {
    const { error } = await supabase
      .from("booking_delivery")
      .delete()
      .eq("id", deliveryId);

    if (error) throw error;
    
    return { error: null };
  } catch (error) {
    console.error("Error deleting delivery info:", error);
    return { error };
  }
};

/**
 * Update supplier information
 */
export const updateBookingSupplier = async (
  supplierId: number,
  supplierData: Partial<{
    name: string;
    email: string;
    phone: string;
    address: string;
    country: string;
    province: string;
  }>
) => {
  try {
    const { data, error } = await supabase
      .from("booking_supplier")
      .update(supplierData)
      .eq("id", supplierId)
      .select();

    if (error) throw error;

    return { data: data?.[0] || null, error: null };
  } catch (error) {
    console.error("Error updating supplier:", error);
    return { data: null, error };
  }
};

/**
 * Link a booking to a container
 */
export const linkBookingToContainer = async (bookingId: number, containerId: number) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({ container_id: containerId })
      .eq("id", bookingId);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error linking booking to container:", error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Link multiple bookings to a container
 */
export const linkMultipleBookingsToContainer = async (bookingIds: number[], containerId: number) => {
  try {
    // Create an array of updates to perform
    const updates = bookingIds.map(bookingId => ({
      id: bookingId,
      container_id: containerId
    }));

    // Perform the batch update
    const { error } = await typedSupabase
      .from("bookings")
      .upsert(updates, { onConflict: 'id' });

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error linking multiple bookings to container:", error);
    return { 
      success: false, 
      error 
    };
  }
}; 